import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import CheckOneIcon from '@components/icons/CheckOne.svg';
import LoadingIcon from '@components/icons/Loading.svg';
import Pencil from '@components/icons/Pencil.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { cn } from '@helpers/cn';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { AdminService, InvitationWithTeams } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { EErrorMessages } from 'app/types/errorMessages';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { sortInvitations } from './helpers/sortInvitations';

import { CellContent } from './styles/CellContent';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { RolesCell } from './styles/RolesCell';
import { TInvitationSortField } from './types/TInvitationSortField';

type TProps = {
  invitations: InvitationWithTeams[];
  onInvitationsChange?: () => Promise<unknown>;
  openInviteUserModalOnClose?: boolean;
};

export const InvitationPage = ({ invitations, onInvitationsChange, openInviteUserModalOnClose = false }: TProps) => {
  const [sortingField, setSortingField] = useState<TInvitationSortField>('createdAt');
  const [sortingReverse, setSortingReverse] = useState(true);
  const sortedInvitations = useMemo(
    () => sortInvitations(invitations, sortingField, sortingReverse),
    [invitations, sortingField, sortingReverse],
  );
  const { call } = useAPI();
  const [isEditingId, setIsEditingId] = useState('');
  const [newEmailValue, setNewEmailValue] = useState('');

  const { openModal, closeModal } = useModalContext();
  const { organization, me } = useAuthContext();

  const toggleSort = (field: TInvitationSortField) => {
    if (sortingField !== field) {
      setSortingField(field);
      setSortingReverse(false);
    } else {
      setSortingReverse((prevState) => !prevState);
    }
  };

  const openDeleteInvitationModal = (id: string) => {
    const organizationId = organization?.id || '';
    openModal(ModalNamesEnum.DeleteInvitationModal, {
      invitationId: id,
      organizationId,
      onSuccess: () => {
        closeModal();
        void onInvitationsChange?.();
      },
      onCancel: () => closeModal(),
      openInviteUserModalOnClose,
    });
  };

  const resendInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      call(() => AdminService.resendInvitation({ invitationId }), {
        onError: (message: string) => {
          toast.error(message || EErrorMessages.DEFAULT);
        },
      }),
    onSuccess: () => {
      toast.success('Invite has been resent');
    },
    onSettled: () => onInvitationsChange?.(),
  });
  const resendingInvitationId = resendInvitationMutation.isPending ? resendInvitationMutation.variables : '';

  const updateInvitationEmailMutation = useMutation({
    mutationFn: ({ invitation, email }: { invitation: InvitationWithTeams; email: string }) =>
      call(() => AdminService.editInvitation({ invitationId: invitation.id, requestBody: { email } }), {
        onError: () => {
          toast.error(EErrorMessages.DEFAULT);
        },
      }),
    onSuccess: (_, { invitation, email }) => {
      setNewEmailValue('');
      setIsEditingId('');
      toast.success('Successfully updated');
      openModal(ModalNamesEnum.ResendInvitationModal, {
        email,
        onSuccess: () => {
          resendInvitationMutation.mutate(invitation.id);
        },
      });
    },
    onSettled: () => onInvitationsChange?.(),
  });

  const updateInvitationEmail = (data: InvitationWithTeams) => {
    if (!newEmailValue.includes('@') || !newEmailValue.includes('.')) {
      toast.error('Email value should be valid');
      return;
    }

    if (data.email === newEmailValue) {
      setNewEmailValue('');
      setIsEditingId('');
      return;
    }

    updateInvitationEmailMutation.mutate({ invitation: data, email: newEmailValue });
  };

  return (
    <TableWrapper>
      <Table>
        <Thead>
          <Tr>
            <SortableTh onClick={() => toggleSort('createdAt')}>
              Created
              {sortingField === 'createdAt' && <SortingDirection reverse={sortingReverse} />}
            </SortableTh>
            <SortableTh onClick={() => toggleSort('email')}>
              Email Address
              {sortingField === 'email' && <SortingDirection reverse={sortingReverse} />}
            </SortableTh>
            <Th>Teams</Th>
            <Th>Status</Th>
            <Th>EXPIRY DATE</Th>
            <Th></Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedInvitations.map((data) => (
            <Tr key={data.id}>
              <Td>
                <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
              </Td>
              <Td>
                <CellContent>
                  {isEditingId === data.id ? (
                    <Input
                      label="email"
                      value={newEmailValue}
                      disabled={isEditingId !== data.id}
                      onChange={(e) => setNewEmailValue(e.target.value)}
                    />
                  ) : (
                    data.email
                  )}

                  {me.roles.includes('ROLE_VS_ADMIN') ? (
                    <div className=" mx-2">
                      {isEditingId !== data.id ? (
                        <IconButtonClean
                          type="button"
                          title="Edit email"
                          onClick={() => {
                            setIsEditingId(data.id);
                            setNewEmailValue(data.email);
                          }}
                        >
                          <Pencil />
                        </IconButtonClean>
                      ) : (
                        <IconButtonClean
                          type="button"
                          title="Save email"
                          disabled={updateInvitationEmailMutation.isPending}
                          onClick={() => updateInvitationEmail(data)}
                        >
                          <CheckOneIcon />
                        </IconButtonClean>
                      )}
                    </div>
                  ) : null}
                </CellContent>
              </Td>
              <Td>
                <CellContent>
                  {(data?.Teams || []).map((team) => (
                    <RolesCell key={team.id}>{team.name}</RolesCell>
                  ))}
                </CellContent>
              </Td>
              <Td>
                <CellContent>{data.status}</CellContent>
              </Td>
              <Td>
                <CellContent>{getAmericanTime(data.expiry)}</CellContent>
              </Td>
              <Td>
                {me.roles.includes('ROLE_ORG_ADMIN') ? (
                  <CellContent>
                    {data.status === 'CREATED' ? (
                      <OutlinedButton
                        className="relative"
                        aria-label="Resend"
                        disabled={resendingInvitationId === data.id}
                        aria-busy={resendingInvitationId === data.id}
                        onClick={() => resendInvitationMutation.mutate(data.id)}
                      >
                        <span className={cn(resendingInvitationId === data.id && 'invisible')}>Resend</span>
                        {resendingInvitationId === data.id ? (
                          <LoadingIcon aria-hidden="true" className="absolute inset-0 m-auto h-3 w-3 animate-spin" />
                        ) : null}
                      </OutlinedButton>
                    ) : null}
                  </CellContent>
                ) : null}
              </Td>
              <Td>
                <CellContent>
                  <IconButtonDelete title="Remove" onClick={() => openDeleteInvitationModal(data.id)}>
                    <TrashBin />
                  </IconButtonDelete>
                </CellContent>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableWrapper>
  );
};
